import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

// COMPONENTS
import BackButton from '../../../common/BackButton';
import CarrierDeviceSingle from './CarrierDeviceSingle';
import CarrierDeviceList from './CarrierDeviceList';
import DeviceCarrierAll from './DeviceCarrier';

// NETWORK
import useDeviceRequest from '../../../../requests/DeviceRequest';

// FORMATTED MESSAGES
import DeviceFormattedMessages from '../../../../messages/admin/DeviceFormattedMessages';

// UTILS
import { validateName, validatePhoneNumber } from '../../../../utils/validate';

export default function DeviceSingleView({
  device,
  isNew,
  backButtonClick,
  errorHandler,
}) {
  // Replace state with useState
  const [isEditing, setIsEditing] = useState(false);
  const [edits, setEdits] = useState({});
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [addNewCarrierDevice, setAddNewCarrierDevice] = useState(false);
  const [types, setTypes] = useState([]);
  const [classes, setClasses] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { getStatusTypeClassData, createDevice, updateDevice, deleteDevice } =
    useDeviceRequest();

  // Replace componentDidMount with useEffect
  useEffect(() => {
    const fetchStatusTypeClassData = async () => {
      const { types, statuses, classes } = await getStatusTypeClassData();
      setTypes(types);
      setStatuses(statuses);
      setClasses(classes);
    };

    fetchStatusTypeClassData();
  }, []);

  // Back button click
  const handleBackButtonClick = () => {
    setSelectedDevice(null);
    setAddNewCarrierDevice(false);
  };

  // Toggle edit mode
  const toggleEdit = () => {
    setIsEditing((prevIsEditing) => !prevIsEditing);
  };

  // Add new carrier device
  const handleAddNewCarrierDevice = () => {
    setAddNewCarrierDevice(true);
  };

  // Show single carrier device
  const showSingleCarrierDevice = (carrierDevice) => {
    console.log('Got carrierDevice:', carrierDevice);
    setSelectedDevice(carrierDevice);
  };

  // Handle form change
  const handleFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    const newValue = type === 'checkbox' ? checked : value;

    console.log('Edits is:', { ...edits, [name]: newValue });

    setEdits({
      ...edits,
      [name]: newValue,
    });
  };

  // Generate error text
  const generateErrorText = () =>
    hasError ? <Alert severity="error">{errorMessage}</Alert> : null;

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    let updateData = edits;
    let result;

    // Handle create new Device
    if (isNew) {
      if (!validateName(updateData.device_name)) {
        return setErrorMessage(DeviceFormattedMessages.errTextDeviceName);
      }
      if (!updateData.device_type) {
        return setErrorMessage(DeviceFormattedMessages.errTextDeviceType);
      }
      if (!updateData.device_class) {
        return setErrorMessage(DeviceFormattedMessages.errTextDeviceClass);
      }
      if (!updateData.device_status) {
        return setErrorMessage(DeviceFormattedMessages.errTextDeviceStatus);
      }
      if (updateData.priority === '') {
        return setErrorMessage(DeviceFormattedMessages.errTextPriority);
      }
      if (isNaN(updateData.priority)) {
        return setErrorMessage(DeviceFormattedMessages.errTextPriorityNumber);
      }
      if (!updateData.primary_device_id) {
        return setErrorMessage(DeviceFormattedMessages.errTextPrimaryDeviceId);
      }
      if (!updateData.route_id) {
        return setErrorMessage(DeviceFormattedMessages.errTextRouteId);
      }
      if (
        updateData.reply_tn &&
        (updateData.reply_tn.trim() === '' ||
          !validatePhoneNumber(updateData.reply_tn))
      ) {
        return setErrorMessage(DeviceFormattedMessages.errTextCheckReplyTn);
      }

      setIsSaving(true);
      result = await createDevice(updateData, errorHandler);
    }
    // Handle update Device
    else {
      setIsSaving(true);
      result = await updateDevice(device.device_id, updateData, errorHandler);
    }

    if (result && !result.error) {
      backButtonClick();
    } else {
      setIsSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    handleCloseConfirmation();
    await deleteDevice(device.device_id, errorHandler);
    window.location.href = '/admin/device'; // Force reload - TODO: redo properly
    setShowConfirmation(false);
  };

  // Close confirmation
  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
  };

  // Generate customer user link
  const generateCustomerUserLink = (userEmail) => {
    if (!userEmail || typeof userEmail === 'undefined') return null;

    return <Typography>Link to Customer User</Typography>;
  };

  // Generate form group
  const generateFormGroup = (label, displayValue) => {
    return (
      <Box sx={{ display: 'flex', gap: 2, marginBottom: 1 }}>
        <Typography fontWeight={700} sx={{ width: 180 }}>
          {label}
        </Typography>
        <Typography>{displayValue}</Typography>
      </Box>
    );
  };

  // Generate edit form group
  const generateEditFormGroup = (
    name,
    label,
    defaultValue,
    required = false
  ) => {
    return (
      <FormGroup>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1}
          alignItems={{ md: 'center' }}
          marginBottom={2}
        >
          <InputLabel required={required} sx={{ minWidth: 200 }}>
            {label}
          </InputLabel>
          <TextField
            name={name}
            type="text"
            defaultValue={defaultValue}
            onChange={handleFormChange}
            size="small"
            fullWidth
            required={required}
          />
        </Stack>
      </FormGroup>
    );
  };

  // Generate edit form checkbox
  const generateEditFormCheckbox = (name, label, defaultChecked) => {
    return (
      <FormGroup sx={{ marginLeft: { md: 27 } }}>
        <FormControlLabel
          control={
            <Checkbox
              defaultChecked={defaultChecked}
              checked={edits.accepts_alphas}
              name={name}
              onChange={handleFormChange}
            />
          }
          label={label}
        />
      </FormGroup>
    );
  };

  // Generate lander
  const generateLander = () => {
    if (selectedDevice || addNewCarrierDevice) return null;

    return (
      <Stack gap={2}>
        <Box>
          <BackButton onClick={backButtonClick} />
        </Box>
        <Typography variant="h4" gutterBottom>
          Device Information
        </Typography>
      </Stack>
    );
  };

  // Generate select
  const generateSelect = (
    options,
    name,
    optionCode,
    value,
    label,
    defaultValue,
    required = false
  ) => {
    return (
      <FormGroup>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1}
          alignItems={{ md: 'center' }}
          marginBottom={2}
        >
          <InputLabel required={required} sx={{ minWidth: 200 }}>
            {label}
          </InputLabel>
          <TextField
            select
            name={name}
            value={edits[name] || defaultValue || ''}
            onChange={handleFormChange}
            fullWidth
            size="small"
            required={required}
          >
            <MenuItem hidden value="">
              Select an option
            </MenuItem>
            {options.map((option) => (
              <MenuItem key={option[optionCode]} value={option[optionCode]}>
                {option[value]}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </FormGroup>
    );
  };

  // Generate carrier device list
  const generateCarrierDeviceList = () => {
    if (isNew || selectedDevice || addNewCarrierDevice) return null;

    return (
      <CarrierDeviceList
        device={device}
        onShow={showSingleCarrierDevice}
        onNew={handleAddNewCarrierDevice}
        errorHandler={errorHandler}
      />
    );
  };

  // Generate body
  const generateBody = () => {
    const buttonText = isEditing ? 'Cancel' : 'Edit';

    // If a Device is selected
    if (selectedDevice) {
      return (
        <CarrierDeviceSingle
          carrierDevice={selectedDevice}
          onBack={handleBackButtonClick}
          errorHandler={errorHandler}
        />
      );
    }

    if (addNewCarrierDevice) {
      return (
        <DeviceCarrierAll
          device={device}
          backButtonClick={handleBackButtonClick}
          errorHandler={errorHandler}
        />
      );
    }

    // Creating new Device
    if (isNew) {
      return (
        <Box maxWidth={800}>
          <form onSubmit={handleSubmit} onChange={handleFormChange}>
            {generateEditFormGroup('device_name', 'Device Name:', null, true)}
            {generateEditFormGroup(
              'copy_from_device_id',
              'Copy From Device:',
              null
            )}
            {generateSelect(
              types,
              'device_type',
              'code',
              'meaning',
              'Device Type',
              null,
              true
            )}
            {generateSelect(
              classes,
              'device_class',
              'code',
              'meaning',
              'Device Class',
              null,
              true
            )}
            {generateSelect(
              statuses,
              'device_status',
              'code',
              'meaning',
              'Device Status',
              null,
              true
            )}
            {generateEditFormGroup('priority', 'Priority:', null, true)}
            {generateEditFormGroup('reply_tn', 'Reply TN:', null)}
            {generateEditFormGroup(
              'primary_device_id',
              'Primary Device Id:',
              null,
              true
            )}
            {generateEditFormGroup('route_id', 'Route Id:', null, true)}
            {generateEditFormCheckbox('accepts_alphas', 'Accepts Alphas', true)}
            {generateEditFormGroup('top_up_credits', 'Top Up Credits:', null)}
            {generateEditFormGroup('last_topped_up', 'Last Topped Up:', null)}
            {generateEditFormGroup(
              'device_sms_count',
              'Current SMS Count:',
              null
            )}
            {generateEditFormGroup(
              'dedicated_user_id',
              'Dedicated User Id:',
              null
            )}
            {generateEditFormGroup(
              'dedicated_cust_id',
              'Dedicated Customer Id:',
              null
            )}
            {generateErrorText()}
            {isSaving && <CircularProgress />}
            {!isSaving && (
              <Button variant="contained" color="secondary" type="submit">
                Save
              </Button>
            )}
          </form>
        </Box>
      );
    }

    // Have Device and not editing
    if (device && !isEditing) {
      return (
        <Box maxWidth={800}>
          <form>
            {generateFormGroup('Device ID:', device.device_id)}
            {generateFormGroup('Device Name:', device.device_name)}
            {generateFormGroup('Device Type:', device.device_type_meaning)}
            {generateFormGroup('Device Class:', device.device_class_meaning)}
            {generateFormGroup('Device Status:', device.device_status_meaning)}
            {generateFormGroup('Device Old Status:', device.old_status_meaning)}
            {generateFormGroup('Priority:', device.priority)}
            {generateFormGroup('Reply TN:', device.reply_tn)}
            {generateFormGroup('Primary Device Id:', device.primary_device_id)}
            {generateFormGroup('Route Id:', device.route_id)}
            {generateFormGroup(
              'Accepts Alphas:',
              device.accepts_alphas === 'Y' ? 'Yes' : 'No'
            )}
            {generateFormGroup('Top Up Credits:', device.top_up_credits)}
            {generateFormGroup('Last Topped Up:', device.last_topped_up)}
            {generateFormGroup('Current SMS Count:', device.device_sms_count)}
            {generateFormGroup(
              'Dedicated User Id:',
              device.dedicated_user_id || 'N/A'
            )}
            {generateFormGroup(
              'Dedicated Customer Id:',
              device.dedicated_cust_id || 'N/A'
            )}
          </form>
          <Button variant="contained" color="secondary" onClick={toggleEdit}>
            {buttonText}
          </Button>
        </Box>
      );
    } else if (device && isEditing) {
      return (
        <Box maxWidth={800}>
          <form onSubmit={handleSubmit} onChange={handleFormChange}>
            {generateEditFormGroup(
              'device_name',
              'Device Name:',
              device.device_name,
              true
            )}
            {generateSelect(
              types,
              'device_type',
              'code',
              'meaning',
              'Device Type',
              device.device_type,
              true
            )}
            {generateSelect(
              classes,
              'device_class',
              'code',
              'meaning',
              'Device Class',
              device.device_class,
              true
            )}
            {generateSelect(
              statuses,
              'device_status',
              'code',
              'meaning',
              'Device Status',
              device.device_status,
              true
            )}
            {generateEditFormGroup(
              'priority',
              'Priority:',
              device.priority,
              true
            )}
            {generateEditFormGroup('reply_tn', 'Reply TN:', device.reply_tn)}
            {generateEditFormGroup(
              'primary_device_id',
              'Primary Device Id:',
              device.primary_device_id,
              true
            )}
            {generateEditFormGroup(
              'route_id',
              'Route Id:',
              device.route_id,
              true
            )}
            {generateEditFormCheckbox(
              'accepts_alphas',
              'Accepts Alphas',
              device.accepts_alphas === 'Y'
            )}
            {generateEditFormGroup(
              'top_up_credits',
              'Top Up Credits:',
              device.top_up_credits
            )}
            {generateEditFormGroup(
              'last_topped_up',
              'Last Topped Up:',
              device.last_topped_up
            )}
            {generateEditFormGroup(
              'device_sms_count',
              'Current SMS Count:',
              device.device_sms_count
            )}
            {generateEditFormGroup(
              'dedicated_user_id',
              'Dedicated User Id:',
              device.dedicated_user_id
            )}
            {generateCustomerUserLink(device.dedicated_user_email)}
            {generateEditFormGroup(
              'dedicated_cust_id',
              'Dedicated Customer Id:',
              device.dedicated_cust_id
            )}
            {generateErrorText()}
            {isSaving && <CircularProgress />}
            {!isSaving && (
              <Button variant="contained" color="secondary" type="submit">
                Save
              </Button>
            )}
          </form>
        </Box>
      );
    }

    return <Typography>(Device) No Data</Typography>;
  };

  // Render
  return (
    <Box>
      {generateLander()}
      {generateBody()}

      <Box sx={{ marginTop: 4 }}>{generateCarrierDeviceList()}</Box>
    </Box>
  );
}
